import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useMetaTags } from '../../hooks/useMetaTags';
import { config } from '../../config';

/**
 * AdminSettings component allows logged-in users to manage admin features
 * 
 * Main features:
 * 1. Enable/disable admin role with password verification
 * 2. Add tokens manually to user wallets
 * 
 * Any authenticated user can become an admin by verifying the admin password.
 * This page is only accessible via direct URL and not included in navigation.
 * The access path is configurable via environment variables:
 * - In .env: ADMIN_PATH and REACT_APP_ADMIN_PATH
 * - Default: "admin"
 */
function AdminSettings() {
  const { t } = useTranslation();
  const { isAdmin, setAdminRole, addTokens, isTokenOperationLoading } = useUser();
  
  // Authentication state
  const [password, setPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const passwordInputRef = useRef<HTMLInputElement>(null);
  
  // Token management state
  const [tokenAmount, setTokenAmount] = useState<number>(0);
  const [tokenInputValue, setTokenInputValue] = useState<string>('');
  const [tokenSuccess, setTokenSuccess] = useState(false);
  const [tokenError, setTokenError] = useState('');
  
  // Set meta tags for the page
  useMetaTags(
    t('admin.title', 'Admin Settings'),
    t('admin.description', 'Manage admin settings for TapDonkey')
  );
  
  // Handle body class and input focus when modal is shown
  React.useEffect(() => {
    if (showPasswordModal) {
      document.body.classList.add('modal-open');
      // Focus the password input when modal is shown
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    } else {
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showPasswordModal]);

  /**
   * Handle user role change
   * Requires password verification when enabling admin
   */
  const handleUserRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value as string;
    const enableAdmin = newRole === 'admin';
    
    if (enableAdmin && !isAdmin) {
      // Trying to enable admin, show password modal
      setShowPasswordModal(true);
    } else if (!enableAdmin && isAdmin) {
      // Disabling admin mode
      setAdminRole(false, '');
    }
  };

  /**
   * Handle password submission for admin access
   */
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setPasswordError('');
    
    // Try to enable admin role with password
    const success = await setAdminRole(true, password);
    
    if (success) {
      setShowPasswordModal(false);
      setPassword('');
    } else {
      setPasswordError(t('settings.passwordError', 'Invalid password'));
    }
  };

  /**
   * Cancel password modal and reset state
   */
  const handleCancelPasswordModal = () => {
    setShowPasswordModal(false);
    setPassword('');
    setPasswordError('');
    
    // Reset the select dropdown to the current user role
    const userRoleSelect = document.getElementById('userRoleSelect') as HTMLSelectElement;
    if (userRoleSelect) {
      userRoleSelect.value = isAdmin ? 'admin' : 'user';
    }
  };

  /**
   * Handle adding tokens to the wallet
   */
  const handleAddTokens = async () => {
    if (tokenAmount <= 0) return;
    
    setTokenError('');
    setTokenSuccess(false);
    
    try {
      const success = await addTokens(tokenAmount);
      
      if (success) {
        setTokenSuccess(true);
        setTokenAmount(0);
        setTokenInputValue('');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setTokenSuccess(false);
        }, 3000);
      } else {
        setTokenError(t('settings.tokenAddFailed', 'Failed to add tokens. Please try again.'));
      }
    } catch (error) {
      setTokenError(t('settings.tokenAddFailed', 'Failed to add tokens. Please try again.'));
    }
  };

  /**
   * Handle token amount change
   */
  const handleTokenAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    setTokenInputValue(inputVal);
    
    // Update the actual token amount (used for validation and submission)
    if (inputVal === '') {
      setTokenAmount(0);
    } else {
      const value = parseInt(inputVal);
      setTokenAmount(isNaN(value) ? 0 : value);
    }
  };

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8">
          <h1 className="h3 mb-4 border-bottom pb-2">{t('admin.title', 'Admin Settings')}</h1>
          
          {/* Alert about this being a hidden page */}
          <div className="alert alert-primary mb-4" role="alert">
            <h4 className="alert-heading">
              <i className="bi bi-shield-lock me-2"></i>
              {t('admin.hiddenPage', 'Admin Control Panel')}
            </h4>
            <p>{t('admin.pageDescription', 'This page allows you to enable admin privileges by verifying the admin password.')}</p>
            <hr />
            <p className="mb-0 small">
              <strong>Current path:</strong> /{config.auth.adminPath}
              <br />
              <span className="text-muted">
                You can change this path by setting ADMIN_PATH and REACT_APP_ADMIN_PATH in your .env file.
              </span>
            </p>
          </div>
          
          {/* User Role Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-body">
              <h2 className="h5 mb-0">{t('admin.userRole', 'User Role Settings')}</h2>
            </div>
            <div className="card-body">
              <div className="mb-2">
                <label htmlFor="userRoleSelect" className="form-label fw-medium">{t('settings.userRole', 'User Role')}</label>
                <select 
                  id="userRoleSelect" 
                  className="form-select" 
                  value={isAdmin ? 'admin' : 'user'}
                  onChange={handleUserRoleChange}
                >
                  <option value="user">{t('settings.regularUser', 'Regular User')}</option>
                  <option value="admin">{t('settings.adminUser', 'Admin User')}</option>
                </select>
                <div className="form-text">
                  {t('settings.userRoleDescription', 'Any user can enable admin mode by verifying the admin password. Admin users can access special features.')}
                </div>
              </div>
              
              {/* Current status display */}
              <div className="mt-4 pt-3 border-top">
                <div className="d-flex align-items-center">
                  <span className="me-2">{t('admin.currentStatus', 'Current Status')}:</span>
                  {isAdmin ? (
                    <span className="badge bg-success">
                      <i className="bi bi-check-circle me-1"></i>
                      {t('admin.adminEnabled', 'Admin Enabled')}
                    </span>
                  ) : (
                    <span className="badge bg-secondary">
                      <i className="bi bi-person me-1"></i>
                      {t('admin.regularUser', 'Regular User')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Token Management Card - Only shown when admin is enabled */}
          {isAdmin && (
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-body">
                <h2 className="h5 mb-0">{t('admin.tokenManagement', 'Token Management')}</h2>
              </div>
              <div className="card-body">
                {/* Add Tokens Manually Section */}
                <div>
                  <h6 className="mb-2">{t('settings.addTokensManually', 'Add Tokens Manually')}</h6>
                  <div className="input-group">
                    <input 
                      type="number" 
                      className="form-control" 
                      value={tokenInputValue}
                      onChange={handleTokenAmountChange}
                      placeholder={t('settings.enterTokenAmount', 'Enter number of tokens')}
                    />
                    <button 
                      className="btn btn-outline-primary" 
                      onClick={handleAddTokens}
                      disabled={tokenAmount <= 0 || isTokenOperationLoading}
                    >
                      {isTokenOperationLoading ? (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      ) : null}
                      {t('settings.addTokens', 'Add Tokens')}
                    </button>
                  </div>
                  
                  {/* Status Messages */}
                  {tokenError && (
                    <div className="alert alert-danger py-2 mt-3" role="alert">
                      {tokenError}
                    </div>
                  )}
                  
                  {tokenSuccess && (
                    <div className="alert alert-success py-2 mt-3" role="alert">
                      {t('settings.tokenAddSuccess', 'Tokens added successfully!')}
                    </div>
                  )}
                  
                  <div className="form-text mt-2">
                    {t('admin.tokenDescription', 'Use this feature to add tokens to your own account. In future versions, admins will be able to manage tokens for all users.')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Password Modal Backdrop */}
      {showPasswordModal && (
        <div className="modal-backdrop show"></div>
      )}
      
      {/* Password Modal */}
      {showPasswordModal && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('settings.enterAdminPassword', 'Enter Admin Password')}</h5>
                <button type="button" className="btn-close" onClick={handleCancelPasswordModal}></button>
              </div>
              <form onSubmit={handlePasswordSubmit}>
                <div className="modal-body">
                  <p className="text-muted mb-3">
                    {t('settings.adminPasswordDescription', 'To enable admin privileges, please enter the admin password.')}
                  </p>
                  <div className="mb-3">
                    <label htmlFor="adminPassword" className="form-label">{t('settings.password', 'Password')}</label>
                    <input
                      type="password"
                      className="form-control"
                      id="adminPassword"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      ref={passwordInputRef}
                      autoFocus
                    />
                    {passwordError && (
                      <div className="text-danger mt-2 small">{passwordError}</div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCancelPasswordModal}>
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {t('common.submit', 'Submit')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSettings; 